import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

// Axios instance for the IPS Scanner API service
const ipsClient = axios.create({
  baseURL: config.api.ipsServiceUrl,
  timeout: 60000,
  headers: {},
});

// Attach JWT token to every request
ipsClient.interceptors.request.use(
  (cfg) => {
    const token = localStorage.getItem('token');
    if (token) {
      cfg.headers.Authorization = `Bearer ${token}`;
    }
    return cfg;
  },
  (error) => Promise.reject(error)
);

// On 401, attempt a token refresh and retry once before logging out
applyAuthRetryInterceptor(ipsClient);

applyTokenRefreshInterceptor(ipsClient);

export const ipsService = {
  /**
   * Fetch the list of all SharePoint client names to scan. Fast call - does not
   * enumerate any files. Used to render the initial "queued" list before
   * scanning clients one at a time.
   *
   * When the backend's IPS_DEBUG_LIMIT_CLIENTS local-debug flag is enabled,
    * `debugLimited` is true and `totalAvailable` reflects the real scannable
    * client count (excluding skipped suffix folders).
    * @returns {Promise<{clients: string[], skippedClients: string[], totalAvailable: number, totalSkipped: number, debugLimited: boolean}>}
   */
  getClients: async () => {
    const response = await ipsClient.get('/ips/clients');
    return response.data;
  },

  /**
   * Scan a single client's SharePoint "Client Services Docs/Contracts" folder
   * (and sub-folders) for Investment Policy Statement documents. Intended to
   * be called once per client so progress can be shown incrementally.
   * @param {string} clientName
   * @returns {Promise<Object>} ClientIpsResult: { clientName, hasIps, files, error }
   */
  scanClient: async (clientName) => {
    const response = await ipsClient.post('/ips/scan-client', { clientName });
    return response.data;
  },

  /**
   * Scan SharePoint clients for Investment Policy Statement (IPS) documents in
   * a single bulk request. Kept for scripted/internal use - prefer
   * getClients() + scanClient() for the UI since this can time out for large
   * client rosters.
   *
   * @param {string[]|null} clientNames - Optional subset of client names to scan.
   *   When omitted, all SharePoint clients are scanned.
   * @returns {Promise<Object>} IpsScanResponse: { success, clientsWithIps,
   *   clientsWithoutIps, totalClients, scannedAt, error }
   */
  scan: async (clientNames = null) => {
    const response = await ipsClient.post('/ips/scan', {
      clientNames: clientNames || undefined,
    });
    return response.data;
  },

  /**
   * Start (or fetch the already-in-progress/completed) background analysis
   * for a single found IPS file. Returns immediately - the actual S3 copy +
   * Bedrock field extraction runs server-side on a background thread and is
   * polled via getFileAnalysis().
   * @param {string} clientName
   * @param {string} fileId
   * @param {string} [fileName]
   * @returns {Promise<Object>} IpsFileAnalysisResult
   */
  analyzeFile: async (clientName, fileId, fileName) => {
    const response = await ipsClient.post('/ips/analyze-file', { clientName, fileId, fileName });
    return response.data;
  },

  /**
   * Poll the status/result of a previously-started file analysis.
   * @param {string} fileId
   * @returns {Promise<Object>} IpsFileAnalysisResult: { fileId, clientName,
   *   fileName, status, fields, rawResponse, error, updatedAt }
   */
  getFileAnalysis: async (fileId) => {
    const response = await ipsClient.get(`/ips/analyze-file/${encodeURIComponent(fileId)}`);
    return response.data;
  },

  /**
   * Start a new full IPS scan run (every SharePoint client), orchestrated
   * server-side by a Step Functions state machine (or a local thread pool in
   * local dev). Returns immediately with a runId - poll getScanRun(runId) or
   * getLatestScanRun() for live progress.
   * @returns {Promise<Object>} StartScanRunResponse: { runId, status, totalClients, startedAt }
   */
  startScanRun: async () => {
    const response = await ipsClient.post('/ips/scan-runs');
    return response.data;
  },

  /**
   * Fetch the most recently started scan run (every client + every matched
   * file's current status), so the UI can resume/show it after a page
   * reload or navigating away and back - even mid-scan.
   * @returns {Promise<Object>} ScanRunDetail
   */
  getLatestScanRun: async () => {
    const response = await ipsClient.get('/ips/scan-runs/latest');
    return response.data;
  },

  /**
   * Poll the full status of one scan run (every client + every matched file).
   * @param {string} runId
   * @returns {Promise<Object>} ScanRunDetail
   */
  getScanRun: async (runId) => {
    const response = await ipsClient.get(`/ips/scan-runs/${encodeURIComponent(runId)}`);
    return response.data;
  },

  /**
   * Re-run scanning/analysis for a subset of clients within an existing scan
   * run (typically ones that previously errored), reusing the same runId.
   * @param {string} runId
   * @param {string[]} clientNames
   * @returns {Promise<Object>} StartScanRunResponse
   */
  retryScanRunClients: async (runId, clientNames) => {
    const response = await ipsClient.post(`/ips/scan-runs/${encodeURIComponent(runId)}/retry`, { clientNames });
    return response.data;
  },

  /**
   * Rename one file from an existing IPS scan run.
   * @param {string} runId
   * @param {{clientName: string, fileId: string, newName: string}} payload
   * @returns {Promise<Object>} RenameRunFileResult
   */
  renameRunFile: async (runId, payload) => {
    const response = await ipsClient.post(
      `/ips/scan-runs/${encodeURIComponent(runId)}/rename-file`,
      payload
    );
    return response.data;
  },

  /**
   * Rename selected files from an existing IPS scan run.
   * @param {string} runId
   * @param {{files: Array<{clientName: string, fileId: string, newName: string}>}} payload
   * @returns {Promise<Object>} RenameRunFilesResponse
   */
  renameRunFiles: async (runId, payload) => {
    const response = await ipsClient.post(
      `/ips/scan-runs/${encodeURIComponent(runId)}/rename-selected`,
      payload
    );
    return response.data;
  },
};

export default ipsService;
